import { Flex, Box, Text, Collapse, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as ArrowDown } from 'assets/arrow-down-icon.svg';
import { ReactComponent as WalletFundIcon } from 'assets/wallet-fund-icon.svg';
import { ReactComponent as ReceiveIcon } from 'assets/receive-icon.svg';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { GlobalContext } from '../../../providers/Global';
import { IncomingTransactionType } from '../../../types/transactions';
import { TokenBalance } from '../../../types/token';
import { ModalType, SCREEN_SM } from 'utils/constants';
import { useMediaQuery } from 'react-responsive';
import { format } from 'date-fns';
import { getExplorerUrl } from '../../../utils/explorerUrl';
import AddressBookModal from 'components/Modals/AddressBookModal';
import IncomingDetails from './details/IncomingDetails';

interface ActivityItemProps {
  transaction: IncomingTransactionType;
  defaultOpen?: boolean;
}

const ActivityItem = ({ transaction, defaultOpen }: ActivityItemProps) => {
  const {
    transferType,
    tx_id,
    consensusTimestamp,
    senderAccountId,
    amount,
    tokenId,
    tokenSymbol,
    tokenName,
    tokenDecimals,
    nftSerialNumber,
    network,
    createdAt,
    type,
  } = transaction;

  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });
  const {
    connection: { accountId },
    openModal,
    setSelectedToken,
  } = useContext(GlobalContext);
  const copyToClipboard = useCopyToClipboard();

  const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });

  const { isOpen: isSaveOpen, onOpen: onSaveOpen, onClose: onSaveClose } = useDisclosure();
  const [saveAddress, setSaveAddress] = useState('');

  const openTokenDetails = () => {
    if (!tokenId) return;
    const token: TokenBalance = {
      token_id: tokenId,
      symbol: tokenSymbol || '',
      name: tokenName || '',
      decimals: tokenDecimals || 0,
      balance: 0,
      type: 'FUNGIBLE_COMMON',
    };
    setSelectedToken(token);
    openModal(ModalType.TokenDetailsModal);
  };

  const formattedAmount = useMemo(() => {
    if (amount == null) return '0';
    let decimals = 0;
    if (transferType === 'hbar') {
      decimals = 8;
    } else if (transferType === 'token' || transferType === 'airdrop') {
      decimals = tokenDecimals ?? 0;
    }
    const raw = String(Math.round(amount));
    if (decimals === 0) return raw;
    if (raw.length <= decimals) {
      const fracStr = raw.padStart(decimals, '0').replace(/0+$/, '');
      return fracStr.length > 0 ? `0.${fracStr}` : '0';
    }
    const whole = raw.slice(0, raw.length - decimals);
    const fracStr = raw.slice(raw.length - decimals).replace(/0+$/, '');
    return fracStr.length > 0 ? `${whole}.${fracStr}` : whole;
  }, [amount, transferType, tokenDecimals]);

  const truncatedAmount = useMemo(() => {
    const dotIdx = formattedAmount.indexOf('.');
    if (dotIdx === -1) return formattedAmount;
    const fracLen = formattedAmount.length - dotIdx - 1;
    if (fracLen <= 6) return formattedAmount;
    return formattedAmount.slice(0, dotIdx + 7).replace(/0+$/, '').replace(/\.$/, '');
  }, [formattedAmount]);

  const displayAmount = useMemo(() => {
    switch (transferType) {
      case 'hbar':
        return `${truncatedAmount} HBAR`;
      case 'token':
      case 'airdrop':
        return `${truncatedAmount} ${tokenSymbol || tokenId || 'Token'}`;
      case 'nft':
        return `${tokenName || tokenId || 'NFT'} #${nftSerialNumber ?? '?'}`;
      default:
        return `${truncatedAmount}`;
    }
  }, [transferType, truncatedAmount, tokenSymbol, tokenId, tokenName, nftSerialNumber]);

  const eventTitle = useMemo(() => {
    switch (transferType) {
      case 'hbar':
        return type === 'Fund' ? 'Funded' : 'Received HBAR';
      case 'token':
        return `Received ${tokenSymbol || 'Token'}`;
      case 'airdrop':
        return `Airdrop ${tokenSymbol || 'Token'}`;
      case 'nft':
        return `Received NFT`;
      default:
        return 'Received';
    }
  }, [transferType, tokenSymbol, type]);

  const renderIcon = useMemo(() => {
    const Icon = type === 'Fund' ? WalletFundIcon : ReceiveIcon;
    return (
      <Box
        w={8}
        h={8}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="primary"
        borderRadius="full"
      >
        <Icon style={{ width: 14, height: 14 }} />
      </Box>
    );
  }, [type]);

  const dateFormat = useCallback(
    (date: Date) => {
      return isScreenSmall
        ? format(date, 'dd MMM yyyy')
        : date.toLocaleTimeString([], {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
    },
    [isScreenSmall],
  );

  const timestamp = useMemo(() => {
    if (consensusTimestamp) {
      const seconds = parseFloat(consensusTimestamp);
      if (!isNaN(seconds)) {
        return new Date(seconds * 1000);
      }
    }
    return new Date(createdAt);
  }, [consensusTimestamp, createdAt]);

  const explorerUrl = useMemo(() => {
    if (!tx_id) return null;
    const formatId = (id: string) => {
      const match = id.match(/^(\d+\.\d+\.\d+)@(\d+)\.(\d+)$/);
      return match ? `${match[1]}-${match[2]}-${match[3]}` : id;
    };
    return getExplorerUrl(network, 'transaction', formatId(tx_id));
  }, [network, tx_id]);

  const senderTruncated = useMemo(() => {
    if (!senderAccountId || senderAccountId === 'unknown') return 'Unknown';
    return senderAccountId;
  }, [senderAccountId]);

  const handleSaveAddress = (address: string) => {
    setSaveAddress(address);
    onSaveOpen();
  };

  return (
    <>
      <Flex
        py={3}
        px={4}
        onClick={onToggle}
        cursor="pointer"
        alignItems="center"
        borderBottom="1px solid"
        borderColor="gray.100"
        _hover={{ bg: 'gray.50' }}
        transition="background 0.15s"
        gap={3}
      >
        {/* Icon */}
        <Box w={10} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
          {renderIcon}
        </Box>

        {/* Event title + sender */}
        <Box flex={2} minW={0}>
          <Text size="xs" fontWeight="500" noOfLines={1}>
            {eventTitle}
          </Text>
          {!isScreenSmall && (
            <Text size="xs" color="blackAlpha.500" noOfLines={1} fontSize="11px">
              From: {senderTruncated}
            </Text>
          )}
        </Box>

        {/* Amount */}
        <Box flex={1} textAlign="right" minW={0}>
          <Text size="xs" fontWeight="500" color="green.600" noOfLines={1}>
            +{displayAmount}
          </Text>
        </Box>

        {/* Timestamp */}
        {!isScreenSmall && (
          <Box flex={1} textAlign="right" minW={0}>
            <Text size="xs" color="blackAlpha.500" noOfLines={1}>
              {dateFormat(timestamp)}
            </Text>
          </Box>
        )}

        {/* Expand arrow */}
        <Box
          transform={isOpen ? 'rotate(180deg)' : 'none'}
          transition="transform 0.2s"
          flexShrink={0}
        >
          <ArrowDown />
        </Box>
      </Flex>

      <Collapse in={isOpen} transition={{ exit: { delay: 0.1 }, enter: { duration: 0.2 } }}>
        <IncomingDetails
          senderAccountId={senderAccountId}
          transferType={transferType}
          formattedAmount={formattedAmount}
          tokenSymbol={tokenSymbol}
          tokenId={tokenId}
          tokenName={tokenName}
          nftSerialNumber={nftSerialNumber}
          txId={tx_id}
          timestamp={timestamp}
          hashScanUrl={explorerUrl}
          onCopy={copyToClipboard}
          onSaveAddress={handleSaveAddress}
          onTokenClick={tokenId ? openTokenDetails : undefined}
        />
      </Collapse>

      <AddressBookModal
        isOpen={isSaveOpen}
        onClose={onSaveClose}
        onSave={() => {}}
        prefillAddress={saveAddress}
        userAccountId={accountId}
      />
    </>
  );
};

export default ActivityItem;
